<template>
  <div v-if="visible" class="modal-backdrop">
    <div class="modal-content upload-modal" role="dialog" aria-modal="true">
      <div class="modal-header">
        <h3>
          <i :class="iconClass"></i>
          {{ title }}
        </h3>
        <button class="close-btn" type="button" @click="close" title="Chiudi">
          &times;
        </button>
      </div>

      <div v-if="errorMessage" class="error-banner">
        <i class="fa-solid fa-circle-exclamation"></i>
        {{ errorMessage }}
      </div>

      <form class="modal-form" @submit.prevent="submit">
        <div class="form-group">
          <label :for="inputId">
            {{ fileLabel }} <span v-if="required" class="required">*</span>
          </label>
          <input
            :id="inputId"
            ref="fileInput"
            type="file"
            :accept="accept"
            :class="{ 'input-error': fileError }"
            @change="handleFileChange"
          />
          <small v-if="fileError" class="field-error">
            {{ fileError }}
          </small>
        </div>

        <div v-if="warningText" class="warning-banner">
          <i class="fa-solid fa-circle-info"></i>
          <span>{{ warningText }}</span>
        </div>

        <div v-if="modeOptions.length" class="form-group">
          <label>
            {{ modeLabel }}
            <span v-if="modeRequired" class="required">*</span>
          </label>

          <div class="radio-group">
            <label
              v-for="option in modeOptions"
              :key="option.value"
              class="radio-label"
            >
              <input
                type="radio"
                :value="option.value"
                v-model="selectedMode"
              />
              <span>{{ option.label }}</span>
            </label>
          </div>

          <div v-if="modeError" class="error-banner inline-error">
            <i class="fa-solid fa-circle-exclamation"></i>
            {{ modeError }}
          </div>
        </div>

        <slot name="extra-fields"></slot>

        <div class="modal-actions">
          <button
            type="button"
            class="btn-action cancel"
            :disabled="uploading"
            @click="close"
          >
            {{ cancelLabel }}
          </button>
          <button
            v-if="showPreview"
            type="button"
            class="btn-action secondary"
            :disabled="uploading"
            @click="$emit('preview')"
          >
            <i class="fas fa-file-excel"></i>
            {{ previewLabel }}
          </button>
          <button
            type="submit"
            class="btn-action primary"
            :disabled="uploading"
          >
            <span v-if="uploading" class="loading-label">
              <span class="spinner"></span>
              {{ uploadingLabel }}
            </span>
            <span v-else>
              <i :class="submitIconClass"></i>
              {{ submitLabel }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "UploadFileModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    iconClass: {
      type: String,
      default: "fas fa-upload",
    },
    inputId: {
      type: String,
      default: "uploadFileInput",
    },
    fileLabel: {
      type: String,
      default: "Seleziona file Excel",
    },
    accept: {
      type: String,
      default: ".xlsx,.xls",
    },
    required: {
      type: Boolean,
      default: true,
    },
    warningText: {
      type: String,
      default: "",
    },
    modeLabel: {
      type: String,
      default: "Come vuoi importare i dati?",
    },
    modeOptions: {
      type: Array,
      default: () => [],
    },
    modeRequired: {
      type: Boolean,
      default: false,
    },
    defaultMode: {
      type: String,
      default: "",
    },
    uploading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    cancelLabel: {
      type: String,
      default: "Annulla",
    },
    submitLabel: {
      type: String,
      default: "Carica e salva",
    },
    submitIconClass: {
      type: String,
      default: "fa-solid fa-cloud-upload-alt",
    },
    uploadingLabel: {
      type: String,
      default: "Caricamento...",
    },
    showPreview: {
      type: Boolean,
      default: false,
    },
    previewLabel: {
      type: String,
      default: "Visualizza Excel",
    },
  },
  data() {
    return {
      selectedFile: null,
      selectedMode: this.defaultMode,
      fileError: "",
      modeError: "",
    };
  },
  watch: {
    visible(value) {
      if (value) {
        this.resetForm();
      }
    },
    defaultMode(value) {
      this.selectedMode = value;
    },
  },
  methods: {
    resetForm() {
      this.selectedFile = null;
      this.selectedMode = this.defaultMode;
      this.fileError = "";
      this.modeError = "";

      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      });
    },
    handleFileChange(event) {
      const file = event.target.files[0] || null;
      this.selectedFile = file;
      if (file) {
        this.fileError = "";
      }
      this.$emit("file-selected", file);
    },
    close() {
      if (this.uploading) return;
      this.resetForm();
      this.$emit("close");
    },
    submit() {
      this.fileError = "";
      this.modeError = "";

      if (this.required && !this.selectedFile) {
        this.fileError = "Seleziona un file da caricare";
        return;
      }

      if (this.modeRequired && this.modeOptions.length && !this.selectedMode) {
        this.modeError = "Seleziona una modalita di importazione";
        return;
      }

      this.$emit("submit", {
        file: this.selectedFile,
        mode: this.selectedMode || null,
      });
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  width: 420px;
  max-width: 100rem;
  min-width: 1rem !important;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.upload-modal {
  width: 590px;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 3rem);
  padding: 0;
  overflow: hidden;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #eee;
  background: #f8fafc;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.35rem;
  color: #1e2937;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #fee2e2;
  color: #ef4444;
}

.modal-form {
  padding: 1.5rem 2rem 2rem;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.25rem;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
}

.required {
  color: #ef4444;
}

.modal-content input {
  width: 90%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.modal-content input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.modal-content input[type="file"] {
  width: 95%;
  padding: 0.6rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
}

.modal-content input[type="file"]:hover {
  border-color: #94a3b8;
}

.modal-content input[type="file"].input-error,
.input-error {
  border-color: #ef4444 !important;
}

.field-error {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.error-banner {
  background: #fee2e2;
  color: #b91c1c;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  margin: 1.25rem 2rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  border-left: 4px solid #ef4444;
  text-align: left;
}

.inline-error {
  margin: 10px 0 0;
  padding: 10px 12px;
}

.warning-banner {
  background: #eff6ff;
  color: #1e3a8a;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.95rem;
  border-left: 4px solid #3b82f6;
  text-align: left;
}

.radio-group {
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.97rem;
  cursor: pointer;
  padding: 0.3rem 0;
}

.radio-label input[type="radio"] {
  width: 12px;
  height: 12px;
  accent-color: #2563eb;
}

.modal-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.75rem;
}

.btn-action {
  border: none;
  border-radius: 8px;
  cursor: pointer;
  min-height: 2.6rem;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
}

.btn-action:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-action.cancel {
  background: #e5e7eb;
  color: #374151;
}

.btn-action.cancel:hover:not(:disabled) {
  background: #d1d5db;
}

.btn-action.primary {
  background: #2563eb;
  color: #fff;
  padding: 0.75rem 2rem;
  font-weight: 600;
}

.btn-action.primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-action.secondary {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.btn-action.secondary:hover:not(:disabled) {
  background: #dcfce7;
}

.loading-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner {
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 620px) {
  .upload-modal {
    width: calc(100vw - 2rem);
  }

  .modal-header,
  .modal-form {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }

  .modal-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}
</style>
